"use client";

import { useState } from "react";
import { Panel, PanelHeader, PanelBody } from "@/shared";
import UserEditPasswordModal from "./UserEditPasswordModal";
import UserEditRoleModal from "./UserEditRoleModal";
import {
  adminUserUpdate,
  UserProvider,
  UserRole,
  UserStates,
} from "@/entities";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

interface Props {
  panelTitle: string;
  data: any;
}
export function AdminUserFrom({ panelTitle, data }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const { id, state } = data.result;

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      state,
    },
  });

  const [roleChange, setRoleChange] = useState(false);
  const roleChangeShow = () => setRoleChange(true);
  const roleChangeClose = () => setRoleChange(false);

  const [passwordChange, setPasswordChange] = useState(false);
  const passwordChangeShow = () => setPasswordChange(true);
  const passwordChangeClose = () => setPasswordChange(false);

  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: adminUserUpdate,
    onSuccess: () => {
      toast.success("회원 정보가 저장되었습니다.");
      queryClient.invalidateQueries({
        queryKey: [`admin-users`, { page: 1, perPage: 15 }],
      });
      queryClient.invalidateQueries({
        queryKey: [`admin-users-${id}`],
      });
      router.push(`/admin/users?${searchParams?.toString()}`);
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onSubmit = async (data: any) => {
    try {
      if (updateMutation.isPending) return;

      await updateMutation.mutateAsync({ id, ...data });
    } catch (e) {
      console.log(e);
      toast.error("에러가 발생했습니다. 다시 시도해 주세요.");
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Panel queryKey={`admin-user-${data.result.id}`}>
          <PanelHeader>{panelTitle}</PanelHeader>
          <PanelBody>
            <div>
              <Controller
                name="state"
                control={control}
                rules={{
                  required: "상태을 입력해주세요.",
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="mb-3">
                    <label className="form-label">상태 *</label>
                    <select
                      className="form-select form-select-md"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                    >
                      {UserStates.filter((item) => item.key !== "delete").map(
                        (item, index) => {
                          return (
                            <option key={index} value={item.key}>
                              {item.value}
                            </option>
                          );
                        }
                      )}
                    </select>
                    {errors.state && (
                      <span className="text-red errorText">
                        {errors.state.message as string}
                      </span>
                    )}
                  </div>
                )}
              />
              <div className="mb-3">
                <label className="form-label">권한</label>
                <button
                  type="button"
                  className="btn btn-md btn-white w-100"
                  onClick={roleChangeShow}
                >
                  {UserRole(data.result.role)} - 권한 변경
                </button>
              </div>
              <div className="mb-3">
                <label className="form-label">가입</label>
                <div className="form-control form-control-md bg-gray-200">
                  {UserProvider(data.result.provider)}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">이메일</label>
                <div className="form-control form-control-md bg-gray-200">
                  {data.result.email}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">휴대전화</label>
                <div className="form-control form-control-md bg-gray-200">
                  {data.result.phone}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">비밀번호</label>
                <button
                  type="button"
                  className="btn btn-md btn-white w-100"
                  onClick={passwordChangeShow}
                >
                  비밀번호 변경
                </button>
              </div>

              <div className="mb-3">
                <label className="form-label">이름</label>
                <div className="form-control form-control-md bg-gray-200">
                  {data.result.name}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">최근 로그인일시</label>
                <div className="form-control form-control-md bg-gray-200">
                  {data.result.login_at
                    ? new Date(data.result.login_at).toLocaleString("ko")
                    : "-"}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">가입일시</label>
                <div className="form-control form-control-md bg-gray-200">
                  {new Date(data.result.created_at).toLocaleString("ko")}
                </div>
              </div>
            </div>
          </PanelBody>
        </Panel>

        <div className="row mb-4">
          <div className="col-4 text-left">
            <Link
              href={{
                pathname: `/admin/users`,
                query: searchParams?.toString(),
              }}
              className="btn btn-white"
              role="button"
            >
              <i className="fa fa-list"></i>
              <span> 목록</span>
            </Link>
          </div>
          <div className="col-4 text-center">
            <button type="submit" className="btn btn-sm btn-inverse">
              <i className="fa fa-check"></i>
              <span> 저장</span>
            </button>
          </div>
          <div className="col-4 text-right"></div>
        </div>
      </form>
      <UserEditRoleModal
        isOpen={roleChange}
        onClose={roleChangeClose}
        role={data.result.role}
        depository_id={data.result.depository_id}
      />
      <UserEditPasswordModal
        isOpen={passwordChange}
        onClose={passwordChangeClose}
      />
    </>
  );
}
